import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import Loader from '../pages/loader'
import { can } from '../../helpers/auth'
import { toast } from 'react-toastify'
import ReusableTable from '../../components/other/ReuseableTable'
import EditCreateCampaignKeywordsModal from '../../components/campaigns/keywords/EditCreateCampaignKeywordsModal'
import { useGetCampaignKeywordsQuery, useLazyGetCampaignKeywordsQuery } from '../../redux/apis/campaignsManagement/keywords/cMKeywordsApiSlice'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import CampaignDropdown from '../../components/CampaignDropdown'
import LanguageDropdown from '../../components/LanguageDropdown'
import DownloadAllDataButton from '../../components/other/DownloadAllDataButton'

const CampaignsKeywords = React.memo(() => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  })

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})

  const [open, setOpen] = useState(false)
  const [holder, setHolder] = useState(null)
  const [isEdit, setIsEdit] = useState(false)

  const context = useContext(AppSettings)

  const { data, isLoading, error, isFetching } = useGetCampaignKeywordsQuery({
    expand: ['last_updated_by', 'campaign', 'language'].join(','),
    page: pagination.current,
    page_size: pagination.pageSize,
    ...(search ? { search } : {}),
    ...filters
  })

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'CAMPAIGN KEYWORD',
      dataIndex: 'campaign_keyword',
      key: 'campaign_keyword',
      filter: true,
      filterType: 'text'
    },
    {
      title: 'CAMPAIGN NAME',
      dataIndex: 'campaign',
      key: 'campaign.campaign_name',
      filter: true,
      filterComponent: CampaignDropdown,
      render: text => <p>{text?.campaign_name}</p>
    },
    {
      title: 'LANGUAGE',
      dataIndex: 'language',
      key: 'language.language_name',
      filter: true,
      filterComponent: LanguageDropdown,
      render: text => <p>{text?.language_name}</p>
    },
    {
      title: 'STATUS',
      dataIndex: 'keyword_status',
      key: 'keyword_status',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
      ],
      render: text => <p>{text ? 'Active' : 'Inactive'}</p>
    },
    {
      title: 'LAST UPDATED BY',
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
      render: text => (
        <p>
          {text?.first_name} {text?.last_name}
        </p>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (text, record) => (
        <div className='flex gap-2'>
          {can('change_campaignkeyword') && (
            <button
              className='btn btn-outline-theme'
              onClick={() => handleEdit(record)}
            >
              Edit
            </button>
          )}
        </div>
      )
    }
  ]

  const handleEdit = record => {
    setOpen(true)
    setHolder(record)
    setIsEdit(true)
  }

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => toast.error(message))
    }
  }, [error])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || isFetching} />

      <div className='flex mb-4'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className='ml-auto flex gap-4'>
          <DownloadAllDataButton
            lazyQueryHook={useLazyGetCampaignKeywordsQuery}
            fileName='campaignskeywords'
          />
          {(can('add_campaignkeyword') || can('change_campaignkeyword')) && (
            <EditCreateCampaignKeywordsModal
              open={open}
              setOpen={setOpen}
              keyword={holder}
              isEdit={isEdit}
              setHolder={setHolder}
              setIsEdit={setIsEdit}
            />
          )}
        </div>
      </div>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
})

export default CampaignsKeywords
